import React, { useState, useMemo, useCallback } from 'react';
import { StatRow, Btn, SectionHeader, AlertBanner, Field, Input } from '../components/UI';
import { calcCaja, promedioDiario, fmtM, fmtDate, todayStr, gcalUrl, totalCostosFijos } from '../utils';

function getPeriodos(n) {
  const today = new Date(); today.setHours(0,0,0,0);
  const fmt = d => d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');

  // Ciclo de INGRESOS: jueves → miércoles
  // Encontrar el último jueves
  const day = today.getDay(); // 0=dom,1=lun,2=mar,3=mie,4=jue,5=vie,6=sab
  const daysFromLastThu = day >= 4 ? day - 4 : day + 3;
  const lastThu = new Date(today.getTime() - daysFromLastThu * 86400000);

  // Ciclo de EROGACIONES: viernes → jueves (empieza 1 día después del inicio de ingresos)
  const lastFri = new Date(lastThu.getTime() + 1 * 86400000);

  const periodos = [];
  for (let i = 0; i < n; i++) {
    // Ingresos: jueves_i → miércoles_i
    const jueresIngreso = new Date(lastThu.getTime() + i * 7 * 86400000);
    const miercIngreso  = new Date(jueresIngreso.getTime() + 6 * 86400000);
    // Erogaciones: viernes_i → jueves_i+1 (vencimiento = jueves_i+1 + 14d)
    const viernesErog   = new Date(lastFri.getTime() + i * 7 * 86400000);
    const juevesErog    = new Date(viernesErog.getTime() + 6 * 86400000);
    const vencimiento   = new Date(juevesErog.getTime() + 14 * 86400000);

    periodos.push({
      idx: i,
      // ingresos
      jueves: fmt(jueresIngreso),
      mierc:  fmt(miercIngreso),
      // erogaciones
      viernes: fmt(viernesErog),
      juevesPago: fmt(juevesErog),
      vencimiento: fmt(vencimiento),
      isCurrent: i === 0
    });
  }
  return periodos;
}


function getDaysUntil(dateStr) {
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.ceil((new Date(dateStr) - today) / 86400000);
}

export default function Flujo({ store, setItem }) {
  const { movimientos, compras, costosMes } = store;
  const [ajuste, setAjuste] = useState(() => parseFloat(localStorage.getItem('flujo-ajuste') || '0'));
  const [simMonto, setSimMonto] = useState('');

  const [depModal, setDepModal] = useState(false);
  const [depForm, setDepForm] = useState({});
  const [ajusteModal, setAjusteModal] = useState(null); // { medio, signo }
  const [ajusteForm, setAjusteForm] = useState({});
  const hoy = todayStr();
  const caja = useMemo(() => calcCaja(movimientos), [movimientos]);
  const prom20 = useMemo(() => promedioDiario(movimientos, 20), [movimientos]);
  const promAjustado = Math.max(0, prom20 - ajuste);
  const periodos = useMemo(() => getPeriodos(5), []);

  function openDeposito() {
    setDepForm({ monto: '', fecha: hoy, obs: '' });
    setDepModal(true);
  }

  function saveDeposito() {
    const monto = parseFloat(depForm.monto);
    if (!monto || monto <= 0) { alert('Ingresá un monto'); return; }
    const id = 'm' + Date.now();
    setItem('movimientos', id + '_out', {
      id: id + '_out', tipo: 'deposito', fecha: depForm.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio: 'efectivo', signo: '-',
      desc: 'Depósito a cuenta' + (depForm.obs ? ' — ' + depForm.obs : ''),
      depositoId: id, autor: 'panel'
    });
    setItem('movimientos', id + '_in', {
      id: id + '_in', tipo: 'deposito', fecha: depForm.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio: 'transferencia', signo: '+',
      desc: 'Depósito recibido en cuenta' + (depForm.obs ? ' — ' + depForm.obs : ''),
      depositoId: id, autor: 'panel'
    });
    setDepModal(false);
  }

  function openAjuste(medio, signo) {
    setAjusteForm({ monto: '', fecha: hoy, obs: '' });
    setAjusteModal({ medio, signo });
  }

  function saveAjuste() {
    const monto = parseFloat(ajusteForm.monto);
    if (!monto || monto <= 0) { alert('Ingresá un monto'); return; }
    const { medio, signo } = ajusteModal;
    const id = 'm' + Date.now();
    setItem('movimientos', id, {
      id, tipo: 'ajuste_caja', fecha: ajusteForm.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio, signo,
      desc: (signo === '+' ? 'Ajuste + ' : 'Ajuste − ') + (medio === 'efectivo' ? 'Efectivo' : 'Cuentas') + (ajusteForm.obs ? ' — ' + ajusteForm.obs : ''),
      autor: 'panel'
    });
    setAjusteModal(null);
  }

  const periodoData = useMemo(() => {
    const today2 = new Date(); today2.setHours(0,0,0,0);
    const fmt = d => d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');

    // Erogaciones pendientes
    const allCompras = Object.values(compras || {}).filter(c => !c.pagado && c.fechaVenc);
    const allCostos  = Object.values(costosMes || {}).filter(c => c.fecha && !c.pagado && !c.esAmortizacion && c.monto > 0);

    // Asignar cada pago al período donde su fecha cae dentro del ciclo vie→jue
    function assignToPeriod(fecha) {
      for (let i = 0; i < periodos.length; i++) {
        const p = periodos[i];
        if (fecha >= p.viernes && fecha <= p.juevesPago) return i;
      }
      // Vencido (antes del primer viernes) → período actual
      if (fecha < periodos[0].viernes) return 0;
      // Futuro más allá del último período → último
      return periodos.length - 1;
    }

    const comprasPorPeriodo = periodos.map(() => []);
    const costosPorPeriodo  = periodos.map(() => []);
    allCompras.forEach(c => comprasPorPeriodo[assignToPeriod(c.fechaVenc)].push(c));
    allCostos.forEach(c  => costosPorPeriodo[assignToPeriod(c.fecha)].push(c));

    let balanceAcum = 0;

    return periodos.map((p, i) => {
      const miercDia = new Date(p.mierc);
      const juevesDia = new Date(p.jueves);

      let diasProyectar;
      if (i === 0) {
        // Período actual: contar días desde hoy hasta miércoles sin ventas cargadas
        let dias = 0;
        for (let d = new Date(today2); d <= miercDia; d = new Date(d.getTime() + 86400000)) {
          const ds = fmt(d);
          const tieneVentas = Object.values(movimientos || {}).some(m => m.tipo === 'venta' && m.fecha === ds);
          if (!tieneVentas) dias++;
        }
        diasProyectar = dias;
      } else {
        diasProyectar = 7;
      }

      const ingresos = diasProyectar * promAjustado;

      const erogCompras = comprasPorPeriodo[i].reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);
      const erogCostos  = costosPorPeriodo[i].reduce((a, c)  => a + (parseFloat(c.monto) || 0), 0);
      const erogaciones = erogCompras + erogCostos;

      // Período actual: base = caja real. Siguientes: base = balance anterior
      const base = i === 0 ? caja.total : balanceAcum;
      balanceAcum = base + ingresos - erogaciones;

      const tieneVencidos = i === 0 && (
        comprasPorPeriodo[0].some(c => c.fechaVenc < hoy) ||
        costosPorPeriodo[0].some(c => c.fecha < hoy)
      );

      return {
        ...p, ingresos, erogCompras, erogCostos, erogaciones,
        balance: balanceAcum, diasProyectar,
        daysUntilVenc: getDaysUntil(p.vencimiento),
        comprasList: comprasPorPeriodo[i],
        costosList: costosPorPeriodo[i],
        tieneVencidos,
      };
    });
  }, [movimientos, compras, costosMes, promAjustado, caja, periodos, hoy]);

  const simulacion = useMemo(() => {
    const monto = parseFloat(simMonto) || 0;
    if (!monto) return null;
    const p = periodoData[0];
    const balanceConCompra = p.balance - monto;
    const necesario = p.erogaciones + monto - caja.total;
    const ingDiarioNec = p.daysUntilVenc > 0 ? necesario / p.daysUntilVenc : 0;
    return { monto, balanceConCompra, viable: balanceConCompra >= 0, diasHastaVenc: p.daysUntilVenc, ingDiarioNec, margen: promAjustado - ingDiarioNec, vencimiento: p.vencimiento };
  }, [simMonto, periodoData, caja, promAjustado]);

  const p0 = periodoData[0];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Efectivo */}
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Efectivo</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace', color: caja.ef >= 0 ? 'var(--grn)' : 'var(--red)', flex: 1 }}>{fmtM(caja.ef)}</div>
            <button onClick={() => openAjuste('efectivo', '+')} title="Sumar efectivo" style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid var(--grn)', background: 'var(--grn-l)', color: 'var(--grn)', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>+</button>
            <button onClick={() => openAjuste('efectivo', '-')} title="Restar efectivo" style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid var(--red)', background: 'var(--red-l)', color: 'var(--red)', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>−</button>
          </div>
        </div>
        {/* Cuentas */}
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Cuentas</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace', color: caja.cuentas >= 0 ? 'var(--blu-m)' : 'var(--red)', flex: 1 }}>{fmtM(caja.cuentas)}</div>
            <button onClick={() => openAjuste('transferencia', '+')} title="Sumar cuentas" style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid var(--grn)', background: 'var(--grn-l)', color: 'var(--grn)', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>+</button>
            <button onClick={() => openAjuste('transferencia', '-')} title="Restar cuentas" style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid var(--red)', background: 'var(--red-l)', color: 'var(--red)', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>−</button>
          </div>
        </div>
        {/* Total */}
        <div style={{ background: 'var(--surf)', border: `2px solid ${caja.total >= 0 ? 'var(--grn)' : 'var(--red)'}`, borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Total disponible</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace', color: caja.total >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(caja.total)}</div>
        </div>
        {/* Promedio y período */}
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Promedio diario 20d</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace' }}>{fmtM(prom20)}</div>
          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>Ajustado: {fmtM(promAjustado)}</div>
        </div>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Período actual vence</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace' }}>{fmtDate(p0.vencimiento)}</div>
          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>{p0.daysUntilVenc}d — cierra {fmtDate(p0.jueves)}</div>
        </div>
      </div>

      {/* Deposito */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={openDeposito}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--blu)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          ↕ Registrar depósito a cuenta
        </button>
      </div>

      {/* Ajuste */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>Ajuste seguridad</div>
        <input type="range" min={0} max={Math.round(prom20)} step={1000} value={ajuste}
          onChange={e => { const v = parseFloat(e.target.value); setAjuste(v); localStorage.setItem('flujo-ajuste', v); }}
          style={{ width: 130 }} />
        <input type="number" value={ajuste} onChange={e => { const n = parseFloat(e.target.value)||0; setAjuste(n); localStorage.setItem('flujo-ajuste', n); }}
          style={{ width: 90, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 13, background: 'var(--surf)', color: 'var(--tx)' }} />
        <div style={{ display: 'flex', gap: 5 }}>
          {[0, 5000, 10000, 20000].map(v => (
            <button key={v} onClick={() => { setAjuste(v); localStorage.setItem('flujo-ajuste', v); }}
              style={{ padding: '3px 10px', borderRadius: 99, border: `1px solid ${ajuste === v ? 'var(--grn)' : 'var(--border2)'}`, background: ajuste === v ? 'var(--grn-l)' : 'var(--surf2)', color: ajuste === v ? 'var(--grn)' : 'var(--tx2)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              {v === 0 ? 'Sin ajuste' : `-${fmtM(v)}`}
            </button>
          ))}
        </div>
      </div>

      {/* Simulador */}
      <div style={{ background: 'var(--surf)', border: `2px solid ${simulacion ? (simulacion.viable ? 'var(--grn)' : 'var(--red)') : 'var(--border)'}`, borderRadius: 'var(--rl)', padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>Simulador — ¿Puedo comprar este monto esta semana?</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Monto hipotético de compra</label>
            <input type="number" value={simMonto} onChange={e => setSimMonto(e.target.value)} placeholder="Ingresá el monto..."
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 16, fontFamily: 'Courier, monospace', fontWeight: 700, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }} />
          </div>
          {simulacion && (
            <div style={{ flex: 2, minWidth: 280, background: simulacion.viable ? 'var(--grn-l)' : 'var(--red-l)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: simulacion.viable ? 'var(--grn)' : 'var(--red)', marginBottom: 8 }}>
                {simulacion.viable ? '✓ VIABLE' : '✗ NO ALCANZA'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--tx2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div>Balance al vencimiento ({fmtDate(simulacion.vencimiento)}): <strong style={{ fontFamily: 'Courier', color: simulacion.viable ? 'var(--grn)' : 'var(--red)' }}>{fmtM(simulacion.balanceConCompra)}</strong></div>
                <div>Días hasta el vencimiento: <strong>{simulacion.diasHastaVenc}d</strong></div>
                <div>Ingreso diario necesario: <strong style={{ fontFamily: 'Courier' }}>{fmtM(simulacion.ingDiarioNec)}/d</strong>
                  {simulacion.margen >= 0
                    ? <span style={{ color: 'var(--grn)', marginLeft: 8 }}>✓ margen +{fmtM(simulacion.margen)}/d</span>
                    : <span style={{ color: 'var(--red)', marginLeft: 8 }}>✗ falta {fmtM(Math.abs(simulacion.margen))}/d</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabla períodos */}
      <SectionHeader title="Proyección por período (ingresos: jue→mié | erogaciones: vie→jue +14d)" />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Período', 'Cierre', 'Vencimiento', 'Proy. fondos', 'Erogaciones', 'Balance', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periodoData.map((p, i) => {
              const esFilaCompra = !p.isCurrent && p.juevesPago === periodoData[0].vencimiento;
              return (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: p.isCurrent ? '#FBF0D8' : esFilaCompra ? 'var(--grn-l)' : p.balance < 0 ? 'var(--red-l)' : '' }}>
                <td style={{ padding: '11px 13px', fontWeight: p.isCurrent || esFilaCompra ? 700 : 400, borderLeft: esFilaCompra ? '3px solid var(--grn)' : 'none' }}>
                  {fmtDate(p.jueves)} — {fmtDate(p.mierc)}
                  {p.isCurrent && <span style={{ marginLeft: 8, fontSize: 9, background: 'var(--yel-l)', color: 'var(--yel)', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>ACTUAL</span>}
                  {esFilaCompra && <span style={{ marginLeft: 8, fontSize: 9, background: 'var(--grn-l)', color: 'var(--grn)', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>COMPRAR AHORA</span>}
                </td>
                <td style={{ padding: '11px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)' }}>{fmtDate(p.juevesPago)}</td>
                <td style={{ padding: '11px 13px' }}>
                  <div style={{ fontFamily: 'Courier, monospace', fontSize: 11 }}>{fmtDate(p.vencimiento)}</div>
                  <div style={{ fontSize: 9.5, color: p.daysUntilVenc <= 7 ? 'var(--red)' : 'var(--tx3)' }}>{p.daysUntilVenc}d</div>
                </td>
                <td style={{ padding: '11px 13px', fontFamily: 'Courier, monospace', color: 'var(--grn)', fontWeight: 600 }}>
                  {fmtM(i === 0 ? caja.total + p.ingresos : p.ingresos)}
                </td>
                <td style={{ padding: '11px 13px' }}>
                  <div style={{ fontFamily: 'Courier, monospace', color: 'var(--red)', fontWeight: 600 }}>-{fmtM(p.erogaciones)}</div>
                  {p.erogCompras > 0 && <div style={{ fontSize: 9.5, color: 'var(--tx3)' }}>Compras: {fmtM(p.erogCompras)}</div>}
                  {p.erogCostos > 0 && <div style={{ fontSize: 9.5, color: 'var(--tx3)' }}>Costos: {fmtM(p.erogCostos)}</div>}
                </td>
                <td style={{ padding: '11px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 14, color: p.balance >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                  {fmtM(p.balance)}
                </td>
                <td style={{ padding: '11px 13px' }}>
                  <a href={gcalUrl('Vencimiento drogueria', p.vencimiento, `Erog: ${fmtDate(p.viernes)} al ${fmtDate(p.juevesPago)}`)} target="_blank" rel="noreferrer"
                    style={{ fontSize: 10, color: 'var(--blu-m)', textDecoration: 'none', padding: '2px 6px', background: 'var(--blu-l)', borderRadius: 4 }}>
                    Calendario
                  </a>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* Detalle pagos del período actual */}
      {(p0.erogCompras > 0 || p0.erogCostos > 0) && (
        <>
          <SectionHeader title={`Pagos del período actual (vence ${fmtDate(p0.vencimiento)})`} />
          {p0.tieneVencidos && (
            <div style={{ background: 'var(--red-l)', border: '1px solid #d9b0ad', borderRadius: 8, padding: '9px 14px', marginBottom: 10, fontSize: 12, color: 'var(--red)', fontWeight: 600 }}>
              🚨 Hay pagos vencidos sin registrar — se arrastran al período actual
            </div>
          )}
          <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
            {p0.comprasList.sort((a,b) => (a.fechaVenc||'').localeCompare(b.fechaVenc||'')).map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, background: c.fechaVenc < hoy ? 'var(--red-l)' : '' }}>
                <div style={{ fontFamily: 'Courier, monospace', fontSize: 11, color: c.fechaVenc < hoy ? 'var(--red)' : 'var(--tx3)', width: 70 }}>{fmtDate(c.fechaVenc)}{c.fechaVenc < hoy ? ' !' : ''}</div>
                <div style={{ flex: 1, fontWeight: 600 }}>{c.proveedor}</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{c.concepto}</div>
                <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(c.monto)}</div>
                <span style={{ fontSize: 9.5, padding: '1px 6px', borderRadius: 99, background: 'var(--surf3)', color: 'var(--tx3)', fontWeight: 700 }}>Compra</span>
              </div>
            ))}
            {p0.costosList.sort((a,b) => (a.fecha||'').localeCompare(b.fecha||'')).map(c => (
              <div key={c.nombre} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, background: c.fecha < hoy ? 'var(--red-l)' : '' }}>
                <div style={{ fontFamily: 'Courier, monospace', fontSize: 11, color: c.fecha < hoy ? 'var(--red)' : 'var(--tx3)', width: 70 }}>{fmtDate(c.fecha)}{c.fecha < hoy ? ' !' : ''}</div>
                <div style={{ flex: 1, fontWeight: 600 }}>{c.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)' }}></div>
                <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(c.monto)}</div>
                <span style={{ fontSize: 9.5, padding: '1px 6px', borderRadius: 99, background: 'var(--blu-l)', color: 'var(--blu-m)', fontWeight: 700 }}>Costo fijo</span>
              </div>
            ))}
          </div>
        </>
      )}
      {ajusteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setAjusteModal(null); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', border: '1px solid var(--border2)', padding: 24, width: 380, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>
                {ajusteModal.signo === '+' ? 'Sumar' : 'Restar'} — {ajusteModal.medio === 'efectivo' ? 'Efectivo' : 'Cuentas'}
              </h3>
              <button onClick={() => setAjusteModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>
            <div style={{ background: ajusteModal.signo === '+' ? 'var(--grn-l)' : 'var(--red-l)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: ajusteModal.signo === '+' ? 'var(--grn)' : 'var(--red)' }}>
              {ajusteModal.signo === '+' ? '↑ Ingresó dinero que no estaba registrado (venta no anotada, aporte externo, etc.)' : '↓ Salió dinero que no estaba registrado (error, diferencia de caja, etc.)'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11, marginBottom: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Monto</label>
                <input type="number" value={ajusteForm.monto} onChange={e => setAjusteForm(f => ({ ...f, monto: e.target.value }))}
                  autoFocus placeholder="0"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 22, fontFamily: 'Courier, monospace', fontWeight: 700, background: 'var(--surf)', color: 'var(--tx)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Fecha</label>
                <input type="date" value={ajusteForm.fecha} onChange={e => setAjusteForm(f => ({ ...f, fecha: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Motivo</label>
                <input type="text" value={ajusteForm.obs} onChange={e => setAjusteForm(f => ({ ...f, obs: e.target.value }))}
                  placeholder="Ej: venta no anotada, aporte..."
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setAjusteModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surf2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--tx)' }}>Cancelar</button>
              <button onClick={saveAjuste} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: ajusteModal.signo === '+' ? 'var(--grn)' : 'var(--red)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                {ajusteModal.signo === '+' ? 'Sumar' : 'Restar'} al saldo
              </button>
            </div>
          </div>
        </div>
      )}

      {depModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setDepModal(false); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', border: '1px solid var(--border2)', padding: 24, width: 380, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Registrar depósito</h3>
              <button onClick={() => setDepModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>
            <div style={{ background: 'var(--blu-l)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--blu-m)' }}>
              ↕ El monto <strong>sale del efectivo</strong> y <strong>entra a cuentas</strong>. El total de caja no cambia.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11, marginBottom: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Monto a depositar</label>
                <input type="number" value={depForm.monto} onChange={e => setDepForm(f => ({ ...f, monto: e.target.value }))}
                  autoFocus placeholder="0"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 22, fontFamily: 'Courier, monospace', fontWeight: 700, background: 'var(--surf)', color: 'var(--tx)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Fecha</label>
                <input type="date" value={depForm.fecha} onChange={e => setDepForm(f => ({ ...f, fecha: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Observaciones</label>
                <input type="text" value={depForm.obs} onChange={e => setDepForm(f => ({ ...f, obs: e.target.value }))}
                  placeholder="Opcional..."
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDepModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surf2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--tx)' }}>Cancelar</button>
              <button onClick={saveDeposito} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--blu)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Confirmar depósito</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
